import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable, catchError, map, of } from "rxjs";
import { environment } from "../../environments/environment";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { ICourse } from "../models/course.model";
import { LessonProgressStatus } from "../models/lesson-progress-status.model";

@Injectable()
export class CourseService{
    Courses$!:Observable<ICourse[]>;
    newCourse$!:Observable<ICourse>;
    createdCourse!:ICourse;

    private http = inject(HttpClient);
    authService = inject(AuthenticationService);
    getCourses(login: string): Observable<ICourse[]> {
        let options = { headers: new HttpHeaders( {
            'Content-type': 'application/json'}) };

        return this.http.get<ICourse[]>( environment.API_URL + `/api/Course/ByUserId/${login}`, options);
    }

    getAllCourses(): Observable<ICourse[]> {
        const token = this.authService.getUserToken();
        const headers = token !== undefined && token !== null && token !== ''
            ? new HttpHeaders({
                'Content-type': 'application/json',
                'Authorization': `Bearer ${token}`
            })
            : new HttpHeaders({
                'Content-type': 'application/json'
            });

        const options = {
            headers
        };

        return this.http.get<unknown>(environment.API_URL + '/api/Course/Catalog', options).pipe(
            map(response => this.normalizeCourseCollection(response)),
            catchError(() => this.http.get<unknown>(environment.API_URL + '/api/Course', options).pipe(
                map(response => this.normalizeCourseCollection(response)),
                catchError(() => this.http.get<unknown>(environment.API_URL + '/api/course', options).pipe(
                    map(response => this.normalizeCourseCollection(response))
                ))
            ))
        );
    }

    getCourse(id: string|undefined ): Observable<ICourse> {
        const token = this.authService.getUserToken();

        let options = { headers: new HttpHeaders( {
            'Content-type': 'application/json',
            'Authorization': `Bearer ${token}`}) };

        return this.http.get<ICourse>( environment.API_URL + `/api/course/ById/${id}`, options);
    }

    getCourseActivity(id: string|undefined ): Observable<ICourse> {
        const token = this.authService.getUserToken();

        let options = { headers: new HttpHeaders( {
            'Content-type': 'application/json',
            'Authorization': `Bearer ${token}`}) };

        return this.http.get<ICourse>( environment.API_URL + `/api/course/myCourseActivity/${id}`, options);
    }


    deleteCourse(id: string): Observable<ICourse> {
        const token = this.authService.getUserToken();

        let options = { headers: new HttpHeaders( {
            'Content-type': 'application/json',
            'Authorization': `Bearer ${token}`}) };

        if (token !== undefined && token !== null && token !== '') {
            return this.http.post<ICourse>( environment.API_URL + `/api/course/delete/${id}`, options);
        } else {
            return of();
        }
    }

    createCourse(course: ICourse): Observable<ICourse> {
        const token = this.authService.getUserToken();

        let options = { headers: new HttpHeaders( {
            'Content-type': 'application/json',
            'Authorization': `Bearer ${token}`}) };

        if (token !== undefined && token !== null && token !== '') {
            return this.http.post<ICourse>( environment.API_URL + '/api/course/Create', course, options);
        } else {
            return of();
        }
    }

    editCourse(course: ICourse): Observable<ICourse> {
        const token = this.authService.getUserToken();

        let options = { headers: new HttpHeaders( {
            'Content-type': 'application/json',
            'Authorization': `Bearer ${token}`}) };

        if (token !== undefined && token !== null && token !== '') {
            return this.http.post<ICourse>( environment.API_URL + '/api/course/update', course, options);
        } else {
            return of();
        }
    }


    myEnrollments(): Observable<ICourse[]> {
        const token = this.authService.getUserToken();

        let options = { headers: new HttpHeaders( {
            'Content-type': 'application/json',
            'Authorization': `Bearer ${token}`}) };

        return this.http.get<ICourse[]>( environment.API_URL + `/api/course/myenrollments`, options);
    }

    searchMyEnrollments(query: string): Observable<ICourse[]> {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) {
            return of([]);
        }

        const normalizedQuery = trimmedQuery.toLowerCase();

        return this.myEnrollments().pipe(
            map(courses => (courses ?? []).filter(course => {
                const title = (course.title ?? '').toLowerCase();
                const headline = (course.headline ?? '').toLowerCase();
                const instructor = (course.instructor ?? '').toLowerCase();

                return (
                    title.includes(normalizedQuery) ||
                    headline.includes(normalizedQuery) ||
                    instructor.includes(normalizedQuery)
                );
            })),
            catchError(() => of([]))
        );
    }

    searchCourses(query: string): Observable<ICourse[]> {
        const trimmedQuery = query.trim();

        if (!trimmedQuery) {
            return of([]);
        }

        const normalizedQuery = trimmedQuery.toLowerCase();

        return this.getAllCourses().pipe(
            map(courses => (courses ?? []).filter(course => {
                const title = (course.title ?? '').toLowerCase();
                const headline = (course.headline ?? '').toLowerCase();
                const instructor = (course.instructor ?? '').toLowerCase();

                return (
                    title.includes(normalizedQuery) ||
                    headline.includes(normalizedQuery) ||
                    instructor.includes(normalizedQuery)
                );
            })),
            catchError(() => this.searchMyEnrollments(trimmedQuery).pipe(
                catchError(() => of([]))
            ))
        );
    }

    private normalizeCourseCollection(payload: unknown): ICourse[] {
        const directArray = this.asCourseArray(payload);
        if (directArray.length > 0) {
            return directArray;
        }

        if (payload === null || payload === undefined || typeof payload !== 'object') {
            return [];
        }

        const wrappedPayload = payload as Record<string, unknown>;
        const candidateKeys = ['items', 'courses', 'data', 'results', 'value', '$values'];

        for (const key of candidateKeys) {
            const normalized = this.normalizeCourseCollection(wrappedPayload[key]);
            if (normalized.length > 0) {
                return normalized;
            }
        }

        const objectValues = Object.values(wrappedPayload);
        const coursesFromValues = objectValues.filter((value): value is ICourse => this.isCourseLike(value));
        return coursesFromValues;
    }

    private asCourseArray(payload: unknown): ICourse[] {
        if (!Array.isArray(payload)) {
            return [];
        }

        return payload.filter((value): value is ICourse => this.isCourseLike(value));
    }

    private isCourseLike(payload: unknown): payload is ICourse {
        if (payload === null || payload === undefined || typeof payload !== 'object') {
            return false;
        }

        const course = payload as Record<string, unknown>;
        return (
            typeof course['id'] === 'string' ||
            typeof course['title'] === 'string' ||
            typeof course['headline'] === 'string'
        );
    }
    
    updateLessonStatus(lessonId: string, status: LessonProgressStatus): Observable<ICourse> {
        const token = this.authService.getUserToken();

        let lesson = {
            lessonId: lessonId,
            lessonProgressStatus: status
        }

        let options = { headers: new HttpHeaders( {
            'Content-type': 'application/json',
            'Authorization': `Bearer ${token}`}) };

        if (token !== undefined && token !== null && token !== '') {
            return this.http.post<ICourse>( environment.API_URL + '/api/lesson/updateStatus', lesson, options);
        } else {
            return of();
        }
    }
}
